import { AxiosResponse } from "axios";
import { axiosInstance } from "./common";

export enum VerificationAnomalyLevel {
  NO_ANOMALY = "NO_ANOMALY",
  MODERATE = "MODERATE",
  CRITICAL = "CRITICAL",
}

export enum VerificationParentEntityType {
  CHARGE = "CHARGE",
}

export enum VehicleRCStatus {
  ACTIVE = "ACTIVE",
  EXPIRED = "EXPIRED",
  SUSPENDED = "SUSPENDED",
  TERMINATED = "TERMINATED",
}

export enum PTVerificationTypes {
  VEHICLE_VALUATION = "VEHICLE_VALUATION",
  VEHICLE_CONDITION = "VEHICLE_CONDITION",
  VEHICLE_EXISTENCE = "VEHICLE_EXISTENCE",
  VEHICLE_REGISTRATION = "VEHICLE_REGISTRATION",
  VEHICLE_CUSTODY = "VEHICLE_CUSTODY",
  VEHICLE_DEALER = "VEHICLE_DEALER",
  VEHICLE_IDENTITY_TAMPER = "VEHICLE_IDENTITY_TAMPER",

  PLANT_MACHINERY_VALUATION = "PLANT_MACHINERY_VALUATION",
  PLANT_MACHINERY_CONDITION = "PLANT_MACHINERY_CONDITION",
  PLANT_MACHINERY_EXISTENCE = "PLANT_MACHINERY_EXISTENCE",
  PLANT_MACHINERY_CUSTODY = "PLANT_MACHINERY_CUSTODY",
  PLANT_MACHINERY_SUPPLIER = "PLANT_MACHINERY_SUPPLIER",
  PLANT_MACHINERY_INSTALLATION = "PLANT_MACHINERY_INSTALLATION",
  PLANT_MACHINERY_SPECIFICATION = "PLANT_MACHINERY_SPECIFICATION",
  PLANT_MACHINERY_IDENTITY_TAMPER = "PLANT_MACHINERY_IDENTITY_TAMPER",

  COMMERCIAL_EQUIPMENT_VALUATION = "COMMERCIAL_EQUIPMENT_VALUATION",
  COMMERCIAL_EQUIPMENT_CONDITION = "COMMERCIAL_EQUIPMENT_CONDITION",
  COMMERCIAL_EQUIPMENT_EXISTENCE = "COMMERCIAL_EQUIPMENT_EXISTENCE",
  COMMERCIAL_EQUIPMENT_CUSTODY = "COMMERCIAL_EQUIPMENT_CUSTODY",
  COMMERCIAL_EQUIPMENT_SUPPLIER = "COMMERCIAL_EQUIPMENT_SUPPLIER",
  COMMERCIAL_EQUIPMENT_INSTALLATION = "COMMERCIAL_EQUIPMENT_INSTALLATION",
  COMMERCIAL_EQUIPMENT_SPECIFICATION = "COMMERCIAL_EQUIPMENT_SPECIFICATION",
  COMMERCIAL_EQUIPMENT_IDENTITY_TAMPER = "COMMERCIAL_EQUIPMENT_IDENTITY_TAMPER",

  PRECIOUS_METAL_VALUATION = "PRECIOUS_METAL_VALUATION",
  PRECIOUS_METAL_AUTHENTICITY = "PRECIOUS_METAL_AUTHENTICITY",
  PRECIOUS_METAL_EXISTENCE = "PRECIOUS_METAL_EXISTENCE",
  PRECIOUS_METAL_CUSTODY = "PRECIOUS_METAL_CUSTODY",

  RESIDENTIAL_REAL_ESTATE_VALUATION = "RESIDENTIAL_REAL_ESTATE_VALUATION",
  RESIDENTIAL_REAL_ESTATE_DIMENSIONS = "RESIDENTIAL_REAL_ESTATE_DIMENSIONS",
  RESIDENTIAL_REAL_ESTATE_EXISTENCE = "RESIDENTIAL_REAL_ESTATE_EXISTENCE",
  RESIDENTIAL_REAL_ESTATE_CUSTODY = "RESIDENTIAL_REAL_ESTATE_CUSTODY",

  COMMERCIAL_REAL_ESTATE_VALUATION = "COMMERCIAL_REAL_ESTATE_VALUATION",
  COMMERCIAL_REAL_ESTATE_DIMENSIONS = "COMMERCIAL_REAL_ESTATE_DIMENSIONS",
  COMMERCIAL_REAL_ESTATE_EXISTENCE = "COMMERCIAL_REAL_ESTATE_EXISTENCE",
  COMMERCIAL_REAL_ESTATE_CUSTODY = "COMMERCIAL_REAL_ESTATE_CUSTODY",

  INDUSTRIAL_REAL_ESTATE_VALUATION = "INDUSTRIAL_REAL_ESTATE_VALUATION",
  INDUSTRIAL_REAL_ESTATE_DIMENSIONS = "INDUSTRIAL_REAL_ESTATE_DIMENSIONS",
  INDUSTRIAL_REAL_ESTATE_EXISTENCE = "INDUSTRIAL_REAL_ESTATE_EXISTENCE",
  INDUSTRIAL_REAL_ESTATE_CUSTODY = "INDUSTRIAL_REAL_ESTATE_CUSTODY",

  AGRICULTURAL_LAND_PARCEL_VALUATION = "AGRICULTURAL_LAND_PARCEL_VALUATION",
  AGRICULTURAL_LAND_PARCEL_DIMENSIONS = "AGRICULTURAL_LAND_PARCEL_DIMENSIONS",
  AGRICULTURAL_LAND_PARCEL_EXISTENCE = "AGRICULTURAL_LAND_PARCEL_EXISTENCE",
  AGRICULTURAL_LAND_PARCEL_CUSTODY = "AGRICULTURAL_LAND_PARCEL_CUSTODY",

  NON_AGRICULTURAL_LAND_PARCEL_VALUATION = "NON_AGRICULTURAL_LAND_PARCEL_VALUATION",
  NON_AGRICULTURAL_LAND_PARCEL_DIMENSIONS = "NON_AGRICULTURAL_LAND_PARCEL_DIMENSIONS",
  NON_AGRICULTURAL_LAND_PARCEL_EXISTENCE = "NON_AGRICULTURAL_LAND_PARCEL_EXISTENCE",
  NON_AGRICULTURAL_LAND_PARCEL_CUSTODY = "NON_AGRICULTURAL_LAND_PARCEL_CUSTODY",

  INDUSTRIAL_ALLOTMENT_LAND_PARCEL_VALUATION = "INDUSTRIAL_ALLOTMENT_LAND_PARCEL_VALUATION",
  INDUSTRIAL_ALLOTMENT_LAND_PARCEL_DIMENSIONS = "INDUSTRIAL_ALLOTMENT_LAND_PARCEL_DIMENSIONS",
  INDUSTRIAL_ALLOTMENT_LAND_PARCEL_EXISTENCE = "INDUSTRIAL_ALLOTMENT_LAND_PARCEL_EXISTENCE",
  INDUSTRIAL_ALLOTMENT_LAND_PARCEL_CUSTODY = "INDUSTRIAL_ALLOTMENT_LAND_PARCEL_CUSTODY",

  RESIDENTIAL_PLOT_LAND_PARCEL_VALUATION = "RESIDENTIAL_PLOT_LAND_PARCEL_VALUATION",
  RESIDENTIAL_PLOT_LAND_PARCEL_DIMENSIONS = "RESIDENTIAL_PLOT_LAND_PARCEL_DIMENSIONS",
  RESIDENTIAL_PLOT_LAND_PARCEL_EXISTENCE = "RESIDENTIAL_PLOT_LAND_PARCEL_EXISTENCE",
  RESIDENTIAL_PLOT_LAND_PARCEL_CUSTODY = "RESIDENTIAL_PLOT_LAND_PARCEL_CUSTODY",

  COMMERCIAL_PLOT_LAND_PARCEL_VALUATION = "COMMERCIAL_PLOT_LAND_PARCEL_VALUATION",
  COMMERCIAL_PLOT_LAND_PARCEL_DIMENSIONS = "COMMERCIAL_PLOT_LAND_PARCEL_DIMENSIONS",
  COMMERCIAL_PLOT_LAND_PARCEL_EXISTENCE = "COMMERCIAL_PLOT_LAND_PARCEL_EXISTENCE",
  COMMERCIAL_PLOT_LAND_PARCEL_CUSTODY = "COMMERCIAL_PLOT_LAND_PARCEL_CUSTODY",

  OTHERS = "OTHERS",
}

export interface PTVerificationFields {
  verification_type: PTVerificationTypes;
  parent_entity_type: VerificationParentEntityType;
  parent_entity_id: string;
  prelogin_details?: Record<string, any>;
  current_details?: Record<string, any>;
  anomaly?: VerificationAnomalyLevel;
}

export interface PTVerification extends PTVerificationFields {
  verification_id: string;
}

export interface UpsertPTVerificationsRequest {
  verifications: PTVerification[];
}

export interface GetPTVerificationsResponse {
  verifications: PTVerification[];
}

export interface MessageResponse {
  message: string;
}

export interface GetPTVerificationsParams {
  case_id: string;
}

export interface UpsertPTVerificationsParams {
  case_id: string;
  request: UpsertPTVerificationsRequest;
}

const PTVerificationsAPI = {
  getPTVerifications: async (params: GetPTVerificationsParams) => {
    const response: AxiosResponse<GetPTVerificationsResponse> =
      await axiosInstance.get(
        `/v1/cases/${params.case_id}/physical-technical-verifications`,
      );

    return response.data;
  },

  upsertPTVerifications: async (
    params: UpsertPTVerificationsParams,
  ) => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(
        `/v1/cases/${params.case_id}/physical-technical-verifications`,
        params.request,
      );

    return response.data;
  },
};

export default PTVerificationsAPI;